import React, { useState } from "react";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';

const Export = ({ data, className = "", apiEndpoint }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showExportType, setShowExportType] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFallbackConfirm, setShowFallbackConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Hardcode the current user
  const currentUser = { username: 'aagneye-syam' };

  // Get current date and time in IST
  const getCurrentDateTimeIST = () => {
    const now = new Date();
    const offset = 5.5 * 60; // IST offset in minutes
    const localTime = now.getTime();
    const localOffset = now.getTimezoneOffset() * 60000;
    const utc = localTime + localOffset;
    const ist = utc + (offset * 60000);
    const istDate = new Date(ist);
    return istDate.toISOString().slice(0, 19).replace('T', ' ');
  };

  // Get current date for filename in IST
  const getFormattedDateIST = () => {
    const now = new Date();
    const offset = 5.5 * 60;
    const localTime = now.getTime();
    const localOffset = now.getTimezoneOffset() * 60000;
    const utc = localTime + localOffset;
    const ist = utc + (offset * 60000);
    const istDate = new Date(ist);
    return istDate.toISOString().slice(0, 10);
  };

  // Get current user
  const getCurrentUser = () => {
    return currentUser.username || 'Unknown User';
  };

  const handleExportClick = (format) => {
    setSelectedFormat(format);
    setShowExportType(true);
  };

  const handleExportTypeSelect = async (exportType) => {
    setLoading(true);
    try {
      if (exportType === 'current') {
        // Export current page data
        switch (selectedFormat) {
          case 'pdf':
            exportPDF(data);
            break;
          case 'excel':
            exportExcel(data);
            break;
          case 'csv':
            exportCSV(data);
            break;
          default:
            break;
        }
      } else {
        // Export all data from API
        if (!apiEndpoint) {
          console.error('API endpoint is not defined');
          alert('Export functionality is not properly configured. Please contact support.');
          return;
        }
        
        // Make sure we're using the correct endpoint format
        const exportUrl = `${apiEndpoint}/export?format=${selectedFormat}`;
        console.log('Exporting from URL:', exportUrl);
        
        try {
          const response = await axios.get(exportUrl);
          
          if (selectedFormat === 'pdf') {
            // For PDF, we need to generate it on the client side
            exportPDF(response.data);
          } else {
            // For other formats, we can use the response directly
            const blob = new Blob([response.data], {
              type: selectedFormat === 'csv' 
                ? 'text/csv' 
                : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `categories_${getFormattedDateIST()}.${selectedFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } catch (error) {
          console.error('Export API error:', error);
          console.error('Error response:', error.response);
          
          // Try to get more details from the error response
          let errorMsg = 'Error exporting data. Please try again.';
          if (error.response && error.response.data) {
            try {
              // If the response is JSON
              if (typeof error.response.data === 'object') {
                errorMsg = error.response.data.error || errorMsg;
              } else {
                // If the response is text
                errorMsg = error.response.data;
              }
            } catch (e) {
              console.error('Error parsing error response:', e);
            }
          }
          
          setErrorMessage(errorMsg);
          setShowFallbackConfirm(true);
        }
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting data. Please try again.');
    } finally {
      setLoading(false);
      if (!showFallbackConfirm) {
        setShowExportType(false);
        setShowOptions(false);
      }
    }
  };

  const handleFallbackExport = () => {
    switch (selectedFormat) {
      case 'pdf':
        exportPDF(data);
        break;
      case 'excel':
        exportExcel(data);
        break;
      case 'csv':
        exportCSV(data);
        break;
      default:
        break;
    }
    setShowFallbackConfirm(false);
    setShowExportType(false);
    setShowOptions(false);
  };

  const handleCancelFallback = () => {
    setShowFallbackConfirm(false);
    setShowExportType(false);
    setShowOptions(false);
  };

  // Export as PDF with standardized look
  const exportPDF = (exportData) => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Add header section
      doc.setFillColor(41, 128, 185);
      doc.rect(0, 0, 210, 30, 'F');

      // Add white text for header
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(20);
      doc.text('IMS Confident Group', 15, 15);
      doc.setFontSize(16);
      doc.text('Category Management Report', 15, 25);

      // Add metadata section
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${getCurrentDateTimeIST()} (IST)`, 15, 40);
      doc.text(`Generated by: ${getCurrentUser()}`, 15, 45);

      // Add horizontal line
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(0.5);
      doc.line(15, 50, 195, 50);

      // Define columns for the table
      const columns = [
        { header: 'Category ID', dataKey: 'CategoryID', width: 30 },
        { header: 'Category Name', dataKey: 'CategoryName', width: 60 },
        { header: 'Normal Days', dataKey: 'NormalDays', width: 30 },
        { header: 'Emergency Days', dataKey: 'EmergencyDays', width: 30 },
      ];

      // Prepare data
      const tableData = exportData.map(item => ({
        CategoryID: item.CategoryID || '',
        CategoryName: item.CategoryName || '',
        NormalDays: item.NormalDays || '',
        EmergencyDays: item.EmergencyDays || '',
      }));

      // Add table with improved styling
      doc.autoTable({
        columns: columns,
        body: tableData,
        startY: 55,
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 12,
          fontStyle: 'bold',
          halign: 'left',
        },
        bodyStyles: {
          fontSize: 10,
          textColor: 0,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { left: 15, right: 15 },
      });

      // Save the PDF
      doc.save(`categories_${getFormattedDateIST()}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Error exporting PDF. Please try again.');
    }
  };

  // Export as Excel
  const exportExcel = (exportData) => {
    try {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Categories');
      XLSX.writeFile(wb, `categories_${getFormattedDateIST()}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Error exporting Excel file. Please try again.');
    }
  };

  // Export as CSV
  const exportCSV = (exportData) => {
    try {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `categories_${getFormattedDateIST()}.csv`;
      link.click();
    } catch (error) {
      console.error('CSV export error:', error);
      alert('Error exporting CSV file. Please try again.');
    }
  };

  return (
    <>
      <button
        onClick={() => setShowOptions(true)}
        className={`bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded ${className}`}
      >
        Export
      </button>

      {showOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Select Export Format</h3>
            <div className="space-y-3">
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => handleExportClick('pdf')}
              >
                Export as PDF
              </button>
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => handleExportClick('excel')}
              >
                Export as Excel
              </button>
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => handleExportClick('csv')}
              >
                Export as CSV
              </button>
              <button
                className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                onClick={() => setShowOptions(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showExportType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Select Export Type</h3>
            <div className="space-y-3">
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => handleExportTypeSelect('current')}
                disabled={loading}
              >
                Export Current Page
              </button>
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => handleExportTypeSelect('all')}
                disabled={loading}
              >
                Export All Categories
              </button>
              <button
                className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                onClick={() => {
                  setShowExportType(false);
                  setShowOptions(false);
                }}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showFallbackConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Export Failed</h3>
            <p className="mb-4 text-red-500">{errorMessage}</p>
            <p className="mb-4">Would you like to export the current page data instead?</p>
            <div className="space-y-3">
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleFallbackExport}
              >
                Export Current Page
              </button>
              <button
                className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                onClick={handleCancelFallback}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Export;